import { ApplicationRef, ComponentRef, effect, inject, Injectable, signal, TemplateRef } from '@angular/core';
import { AppComponent } from '../../app.component';
import { ModalComponent } from './modal.component';
import { IModalOptions } from './interfaces/IModalOptions';

/**
 * Opens modals on demand from an `ng-template` (with an optional context object)
 */
@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly AppRef = inject(ApplicationRef);
  private CurrentModal = signal<ComponentRef<ModalComponent> | undefined>(undefined);

  /**
   * Shows a modal onto the page. Modals are not allowed to stack. Calling this
   * while another is displayed will close that modal first.
   */
  public ShowModal(
    modalContents: TemplateRef<object>,
    context: object | undefined = undefined,
    options: IModalOptions = {}
  ): ComponentRef<ModalComponent> {
    if (this.CurrentModal() !== undefined) {
      this.CloseCurrentModal();
    }

    const modalComponentRef = this.GetApp().ModalsContainer().createComponent(ModalComponent);
    modalComponentRef.setInput("Contents", modalContents);
    modalComponentRef.setInput("ContentsContext", context);

    if (options.Size !== undefined) {
      modalComponentRef.setInput("Size", options.Size);
    }
    if (options.ShouldFadeInAndOut !== undefined) {
      modalComponentRef.setInput("ShouldFadeInAndOut", options.ShouldFadeInAndOut);
    }
    if (options.OnCloseRequested !== undefined) {
      const onCloseRequested = options.OnCloseRequested;
      modalComponentRef.setInput("InterceptCloseRequests", true);
      modalComponentRef.instance.OnCloseRequested.subscribe(() => {
        onCloseRequested();
      });
    }
    if (typeof options.PreventClosing === "function") {
      const preventClosingSignal = options.PreventClosing;
      effect(() => {
        modalComponentRef.setInput("PreventClosing", preventClosingSignal());
      }, { injector: modalComponentRef.injector });
    } else if (options.PreventClosing !== undefined) {
      modalComponentRef.setInput("PreventClosing", options.PreventClosing);
    }

    modalComponentRef.instance.OnClosed.subscribe(() => {
      if (this.CurrentModal() === modalComponentRef) {
        modalComponentRef.destroy();
        this.CurrentModal.set(undefined);
      }
    });

    this.CurrentModal.set(modalComponentRef);

    // Give the newly-created component a render pass before opening it, 
    // so the fade-in animation has an initial (closed) state to transition from.
    setTimeout(() => {
      modalComponentRef.instance.Open();
    }, 15);

    return modalComponentRef;
  }

  /**
   * Closes the current modal.
   */
  public CloseCurrentModal(): void {
    this.CurrentModal()?.instance.Close();
  }

  private GetApp(): AppComponent {
    return this.AppRef.components[0].instance as AppComponent;
  }
}
